<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { PhArrowLeft } from "@phosphor-icons/vue";

import type { ValidationRule } from "@/types/utils/validators.type.ts";
import validators from "@/utils/validators.ts";

const { t } = useI18n();
type Step = "issue" | "form" | "email";

const props = defineProps<{
  loading?: boolean;
}>();

const step2FormRef = ref();
const step3FormRef = ref();
const isStep2Valid = ref(false);
const isStep3Valid = ref(false);

const requiredRules = ref<ValidationRule[]>([validators.requiredRule]);
const emailRules = ref<ValidationRule[]>([...validators.emailRules]);
const requiredEmailRules = ref<ValidationRule[]>([
  validators.requiredRule,
  ...validators.emailRules,
]);
const emit = defineEmits<{
  (
    e: "confirmRepair",
    payload: {
      issue: string;
      enterprise_name: string;
      contact_name: string;
      contact_email: string;
      contact_phone: string;
      address: string;
      access_instructions: string;
      email: string;
    },
  ): void;
}>();

const step = ref<Step>("issue");

const issue = ref("");
const email = ref("");

const form = ref({
  enterprise_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  access_instructions: "",
});

const nextToForm = () => {
  if (!issue.value.trim()) return;
  step.value = "form";
};

const goBack = () => {
  if (step.value === "form") {
    step.value = "issue";
  } else {
    step.value = "form";
  }
};

const endForm = async () => {
  if (!issue.value.trim()) return;

  const { valid } = await step2FormRef.value.validate();
  if (!valid) return;

  step.value = "email";
};

const submit = async () => {
  if (props.loading) return;

  const { valid } = await step3FormRef.value.validate();
  if (!valid) return;

  emit("confirmRepair", {
    issue: issue.value,
    ...form.value,
    email: email.value,
  });
};
</script>

<template>
  <div class="repair-sheet">
    <!-- STEP 1 -->
    <div v-if="step === 'issue'">
      <h2 class="mb-2" style="line-height: 1.2">
        {{ t("views.captureView.repair.issueTitle") }}
      </h2>
      <p class="mb-6">{{ t("views.captureView.repair.issueSubtitle") }}</p>

      <v-textarea
        v-model="issue"
        rows="4"
        :label="t('views.captureView.repair.issue')"
        :placeholder="t('views.captureView.repair.issuePlaceholder')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        hide-details="auto"
      />

      <v-btn
        block
        class="btn-primary btn-pill mt-6"
        :disabled="!issue"
        @click="nextToForm"
      >
        {{ t("views.captureView.repair.continue") }}
      </v-btn>
    </div>

    <!-- STEP 2 -->
    <div class="form" v-if="step === 'form'">
      <h2 class="mb-2" style="line-height: 1.2">
        {{ t("views.captureView.repair.formTitle") }}
      </h2>

      <div class="header">
        <v-btn icon variant="text" :disabled="loading" @click="goBack">
          <PhArrowLeft size="20" />
        </v-btn>

        <h2 class="title">
          {{ t("views.captureView.repair.contactTitle") }}
        </h2>
      </div>

      <v-form
        ref="step2FormRef"
        v-model="isStep2Valid"
        @submit.prevent="endForm"
        class="form"
      >
        <v-text-field
          v-model="form.enterprise_name"
          :label="t('views.captureView.repair.enterprise')"
          :placeholder="t('views.captureView.repair.enterprise')"
          :rules="requiredRules"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          hide-details="auto"
        />

        <v-text-field
          v-model="form.contact_name"
          :label="t('views.captureView.repair.contactName')"
          :placeholder="t('views.captureView.repair.contactName')"
          :rules="requiredRules"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          hide-details="auto"
        />

        <v-text-field
          v-model="form.contact_email"
          :label="t('views.captureView.repair.email')"
          :placeholder="t('views.captureView.repair.email')"
          :rules="requiredEmailRules"
          type="email"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          hide-details="auto"
        />

        <v-text-field
          v-model="form.contact_phone"
          :label="t('views.captureView.repair.phone')"
          :placeholder="t('views.captureView.repair.phone')"
          :rules="requiredRules"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          hide-details="auto"
        />

        <v-text-field
          v-model="form.address"
          :label="t('views.captureView.repair.address')"
          :placeholder="t('views.captureView.repair.addressPlaceholder')"
          :rules="requiredRules"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          hide-details="auto"
        />

        <v-textarea
          v-model="form.access_instructions"
          :label="t('views.captureView.repair.accessInstructions')"
          :placeholder="t('views.captureView.repair.accessInstructionsPlaceholder')"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          hide-details="auto"
        />
      </v-form>

      <v-btn
        block
        class="btn-primary btn-pill mt-6"
        :disabled="!isStep2Valid"
        @click="endForm"
      >
        {{ t("views.captureView.repair.submit") }}
      </v-btn>
    </div>

    <div class="form" v-if="step === 'email'">
      <h2 class="mb-2" style="line-height: 1.2">
        {{ t("views.captureView.repair.emailTitle") }}
      </h2>

      <div class="header">
        <v-btn icon variant="text" :disabled="loading" @click="goBack">
          <PhArrowLeft size="20" />
        </v-btn>

        <h2 class="title">
          {{ t("views.captureView.repair.emailInCharge") }}
        </h2>
      </div>

      <v-form
        ref="step3FormRef"
        v-model="isStep3Valid"
        @submit.prevent="submit"
      >
        <v-text-field
          v-model="email"
          :label="t('views.captureView.repair.emailInChargePlaceholder')"
          :placeholder="t('views.captureView.repair.emailInChargePlaceholder')"
          :rules="emailRules"
          type="email"
          variant="outlined"
          density="comfortable"
          rounded="lg"
        />
      </v-form>

      <v-btn
        block
        class="btn-primary btn-pill mt-6"
        :disabled="loading || !isStep3Valid"
        :loading="loading"
        @click="submit"
      >
        {{ t("views.captureView.repair.submit") }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
.repair-sheet {
  padding-bottom: 8px;
}

.header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: unset !important;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.title-header {
  line-height: 2rem;
}
</style>
